import { useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import React, { useRef } from 'react';
import AuthLayout from '../components/layout/AuthLayout.jsx';
import BaseCard from '../components/misc/BaseCard.jsx';
import { useUpdatePictureMutation, useUpdateProfileMutation } from '../api/user.js';
import { PROFILE_SCHEMA } from '../utils/user.js';
import { useErrors } from '../hooks/useErrors.js';
import BaseInput from '../components/form/BaseInput.jsx';
import BaseButton from '../components/form/BaseButton.jsx';
import IconImageEdit from '../components/images/IconImageEdit.jsx';
import SocialLinks from '../components/misc/SocialLinks.jsx';

const Account = () => {
    const user = useSelector((state) => state.user.user);
    const [updateProfile, { isLoading, error }] = useUpdateProfileMutation();
    const [updatePicture] = useUpdatePictureMutation();

    const fileInputRef = useRef();
    const navigate = useNavigate();

    const { handleSubmit, getValues, formState, control, clearErrors } = useForm({
        resolver: yupResolver(PROFILE_SCHEMA),
        defaultValues: {
            name: (user && user.name) || '',
            email: (user && user.email) || '',
            phone: (user && user?.phone) || '',
            job_title: (user && user?.job_title) || '',
            company: (user && user?.company) || '',
            bio: (user && user?.bio) || '',
            current_password: '',
            password: '',
            password_confirmation: '',
            social_links: (user && user?.social_links) || []
        },
        reValidateMode: 'onSubmit'
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'social_links'
    });

    const { errors } = useErrors(error?.message, formState);

    const sendUpdateProfile = async (requestData) => {
        await updateProfile(requestData);
    };

    const handleAvatarChange = (event) => {
        const file = event.target.files?.[0];

        if (file) {
            updatePicture({
                picture: file
            });

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const formValues = getValues();

    return (
        <AuthLayout title='My Profile' backRoute='/dashboard'>
            <div className='max-w-3xl w-full mx-auto px-8'>
                <BaseCard>
                    <div className='flex flex-row items-center'>
                        <div className='mr-4 relative'>
                            {user && (
                                <div className='rounded-full w-20 h-20 overflow-hidden'>
                                    {user.avatar && <img src={user.avatar} alt='Avatar' />}
                                    {!user.avatar && (
                                        <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                            <div className='text-theme1-primary font-bold text-3xl'>
                                                {user.name
                                                    .split(' ')
                                                    .map((n) => n.substring(0, 1).toUpperCase())
                                                    .join('')}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                className='absolute top-0 right-[-15px] cursor-pointer'
                            >
                                <IconImageEdit className='w-6 h-auto fill-theme1-dark' />
                                <input
                                    type='file'
                                    ref={fileInputRef}
                                    className='hidden'
                                    onChange={handleAvatarChange}
                                    accept='image/*'
                                />
                            </div>
                        </div>
                        <div className='flex-1'>
                            {user && user.name && (
                                <div className='text-theme1-primary font-bold text-2xl'>{user.name}</div>
                            )}
                            {user && user.job_title && (
                                <div className='text-sm text-theme1-primary2 font-bold mt-1 opacity-50'>
                                    {user.job_title}
                                </div>
                            )}
                            {user && user.company && (
                                <div className='mt-2 text-base font-bold text-theme1-text2'>{user.company}</div>
                            )}
                        </div>
                    </div>
                </BaseCard>
                <div className='mb-6 text-theme1-primary font-bold text-xl'>Account Details</div>
                <div className='mb-10'>
                    <BaseInput
                        type='text'
                        control={control}
                        label='Name'
                        name='name'
                        clearErrors={clearErrors}
                        errors={[...(errors?.name ? errors.name : [])]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='text'
                        control={control}
                        label='Email'
                        name='email'
                        clearErrors={clearErrors}
                        errors={[...(errors?.email ? errors.email : [])]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='text'
                        control={control}
                        label='Phone'
                        name='phone'
                        clearErrors={clearErrors}
                        errors={[]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='text'
                        control={control}
                        label='Job Title'
                        name='job_title'
                        clearErrors={clearErrors}
                        errors={[]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='text'
                        control={control}
                        label='Company'
                        name='company'
                        clearErrors={clearErrors}
                        errors={[]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='text'
                        control={control}
                        label='Bio'
                        name='bio'
                        clearErrors={clearErrors}
                        errors={[]}
                    />
                </div>
                <div className='mb-6 text-theme1-primary font-bold text-xl'>Security</div>
                <div className='mb-10'>
                    <BaseInput
                        type='password'
                        control={control}
                        label='Current password'
                        name='current_password'
                        clearErrors={clearErrors}
                        errors={[...(errors?.current_password ? errors.current_password : [])]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='password'
                        control={control}
                        label='New password'
                        name='password'
                        clearErrors={clearErrors}
                        errors={[...(errors?.password ? errors.password : [])]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='password'
                        control={control}
                        label='Rewrite password'
                        name='password_confirmation'
                        clearErrors={clearErrors}
                        errors={[]}
                    />
                </div>
                <SocialLinks
                    addNew={user && user?.social_links?.length === 0}
                    fields={fields}
                    control={control}
                    append={append}
                    remove={remove}
                    clearErrors={clearErrors}
                />
                <div className='mb-10'>
                    <div className='grid grid-cols-2 gap-4'>
                        <BaseButton
                            id='btn-submit'
                            className='btn-primary'
                            onClick={handleSubmit(sendUpdateProfile)}
                            isLoading={isLoading}
                            textValue='Save'
                            disabled={!formValues.email || !formValues.name}
                        />

                        <BaseButton
                            id='btn-cancel'
                            className='btn-primary-outline'
                            onClick={() => navigate('/')}
                            textValue='Cancel'
                        />
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};

export default Account;
